import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Payment.css';
import userIcon from '../../assets/partner/USER.png';
import emailIcon from '../../assets/partner/EMAIL.png';
import locationIcon from '../../assets/partner/LOCATION.png';
import phoneIcon from '../../assets/partner/PHONE.png';
import calendarIcon from '../../assets/partner/CALENDER.png';
import timeIcon from '../../assets/partner/TIME.png';
import dropdownArrow from '../../assets/partner/dropdown.png';
import backArrow from '../../assets/partner/dropdown.png';
import PaymentSuccessDialog from './PaymentSuccessDialog';
import { ProfessionalService } from '../../services/ProfessionalService';

// Header Component
const Header: React.FC<{
  showBackButton?: boolean;
  onBackClick?: () => void;
  title?: string;
  subtitle?: string;
}> = ({
  showBackButton = true,
  onBackClick,
  title = 'Checkout',
  subtitle = 'Securely Payment',
}) => {
  const navigate = useNavigate();
  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };
  return (
    <header className="payment-header">
      {showBackButton && (
        <div className="payment-header-back">
          <button
            onClick={handleBackClick}
            className="payment-header-back-button"
          >
            <img
              src={backArrow}
              alt="Back arrow"
              className="payment-header-back-arrow"
            />
            <span className="payment-header-back-text">
              Back
            </span>
          </button>
        </div>
      )}
      <div className="payment-header-title">
        <h2 className="payment-header-subtitle">
          {subtitle}
        </h2>
        <h1 className="payment-header-main-title">
          {title}
        </h1>
      </div>
    </header>
  );
};

// Button Component
const Button: React.FC<{
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  children: React.ReactNode;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  children,
  className = '',
  ...props
}) => {
  const buttonClasses = [
    'payment-button',
    `payment-button-${variant}`,
    `payment-button-${size}`,
    fullWidth ? 'payment-button-full' : '',
    className
  ].join(' ').trim();

  return (
    <button
      className={buttonClasses}
      {...props}
    >
      {children}
    </button>
  );
};

// Dropdown Component
interface DropdownOption {
  value: string;
  label: string;
}
const Dropdown: React.FC<{
  options: DropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  fullWidth?: boolean;
  className?: string;
}> = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  label,
  error,
  fullWidth = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const selectedOption = options.find((option) => option.value === value);
  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const dropdownClasses = [
    'payment-dropdown',
    error ? 'payment-dropdown-error' : '',
    className
  ].join(' ').trim();

  return (
    <div className={fullWidth ? 'payment-dropdown-container' : ''}>
      {label && (
        <label className="payment-dropdown-label">
          {label}
        </label>
      )}
      <div ref={dropdownRef} className={dropdownClasses}>
        <button
          type="button"
          className="payment-dropdown-button"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="payment-dropdown-selected">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <img
            src={dropdownArrow}
            alt="Dropdown arrow"
            className={`payment-dropdown-arrow ${isOpen ? 'payment-dropdown-arrow-open' : ''}`}
          />
        </button>
        {isOpen && (
          <div className="payment-dropdown-menu">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="payment-dropdown-option"
                onClick={() => handleOptionClick(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <p className="payment-error-message">{error}</p>}
    </div>
  );
};

// InputField Component
const InputField: React.FC<{
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  className?: string;
} & React.InputHTMLAttributes<HTMLInputElement>> = ({
  label,
  error,
  helperText,
  fullWidth = true,
  className = '',
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  
  const inputClasses = [
    'payment-input',
    error ? 'payment-input-error' : '',
    className
  ].join(' ').trim();

  return (
    <div className={fullWidth ? 'payment-input-container' : ''}>
      {label && (
        <label className="payment-input-label">
          {label}
        </label>
      )}
      <input
        className={inputClasses}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...props}
      />
      {error && <p className="payment-error-message">{error}</p>}
      {helperText && !error && <p className="payment-helper-text">{helperText}</p>}
    </div>
  );
};

// Main Checkout Component
interface PaymentInfo {
  cardNumber: string;
  expirationDate: string;
  securityCode: string;
  paymentMethod: string;
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;

  // If no state, redirect to booking page
  useEffect(() => {
    if (!state || !state.appointmentData || !state.customerData) {
      navigate('/book-doctor/' + (state?.appointmentData?.doctorId || ''), { replace: true });
    }
  }, [state, navigate]);

  const appointmentData = state?.appointmentData;
  const customerData = state?.customerData;
  const paymentMethods = [
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ];
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
    cardNumber: '',
    expirationDate: '',
    securityCode: '',
    paymentMethod: '',
  });
  const [errors, setErrors] = useState<Partial<PaymentInfo>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);

  const handlePaymentInfoChange = (field: keyof PaymentInfo, value: string) => {
    setPaymentInfo((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };
  const validateForm = (): boolean => {
    const newErrors: Partial<PaymentInfo> = {};
    if (!paymentInfo.paymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method';
    }
    if (!paymentInfo.cardNumber) {
      newErrors.cardNumber = 'Card number is required';
    } else if (paymentInfo.cardNumber.length < 16) {
      newErrors.cardNumber = 'Card number must be at least 16 digits';
    }
    if (!paymentInfo.expirationDate) {
      newErrors.expirationDate = 'Expiration date is required';
    }
    if (!paymentInfo.securityCode) {
      newErrors.securityCode = 'Security code is required';
    } else if (paymentInfo.securityCode.length < 3) {
      newErrors.securityCode = 'Security code must be at least 3 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handlePayment = async () => {
    if (!validateForm()) {
      return;
    }
    setIsProcessing(true);
    try {
      // Call createConsultation API
      await ProfessionalService.createConsultation({
        professionnel_id: appointmentData.doctorId,
        date: appointmentData.date,
        heure: appointmentData.time,
        nom_patient: customerData.name,
        email: customerData.email,
        pays: customerData.location,
        telephone: customerData.phone,
        sujet: customerData.subject || '',
      });
      setShowSuccessDialog(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setShowSuccessDialog(false);
      setPaymentDone(true);
    } catch (error) {
      alert('Payment or booking failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  const handleBackClick = () => {
    navigate(-1);
  };
  return (
    <div className="payment-container">
      {showSuccessDialog && (
        <PaymentSuccessDialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)} />
      )}
      <div className="payment-wrapper">
        <Header
          showBackButton={true}
          onBackClick={handleBackClick}
          title="Checkout"
          subtitle="Securely Payment"
        />
        <div className="payment-main-layout">
          {/* Appointment Summary Section */}
          <div className="payment-summary-section">
            <div className="payment-summary-card">
              {/* Subtotal */}
              <div className="payment-summary-subtotal">
                <h2 className="payment-summary-subtotal-title">
                  <span>Subtotal </span>
                  <span className="payment-summary-subtotal-bold">: </span>
                  <span className="payment-summary-subtotal-bold">{appointmentData.subtotal}</span>
                </h2>
                <p className="payment-summary-doctor">
                  {appointmentData.doctorName}
                </p>
              </div>
              {/* Appointment Details */}
              <div className="payment-summary-details">
                <div className="payment-summary-detail">
                  <img
                    src={calendarIcon}
                    alt="Calendar"
                    className="payment-summary-icon"
                  />
                  <span className="payment-summary-text">
                    {appointmentData.date}
                  </span>
                </div>
                <div className="payment-summary-detail">
                  <img
                    src={timeIcon}
                    alt="Time"
                    className="payment-summary-icon"
                  />
                  <span className="payment-summary-text">
                    {appointmentData.time}
                  </span>
                </div>
              </div>
              {/* Divider */}
              <div className="payment-divider"></div>
              {/* Customer Information */}
              <div className="payment-summary-details">
                <div className="payment-summary-detail">
                  <img
                    src={userIcon}
                    alt="User"
                    className="payment-summary-icon"
                  />
                  <span className="payment-summary-text">
                    {customerData.name}
                  </span>
                </div>
                <div className="payment-summary-detail">
                  <img
                    src={emailIcon}
                    alt="Email"
                    className="payment-summary-icon"
                  />
                  <span className="payment-summary-text">
                    {customerData.email}
                  </span>
                </div>
                <div className="payment-summary-detail">
                  <img
                    src={locationIcon}
                    alt="Location"
                    className="payment-summary-icon"
                  />
                  <span className="payment-summary-text">
                    {customerData.location}
                  </span>
                </div>
                <div className="payment-summary-detail">
                  <img
                    src={phoneIcon}
                    alt="Phone"
                    className="payment-summary-icon"
                  />
                  <span className="payment-summary-text">
                    {customerData.phone}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Payment Section */}
          <div className="payment-form-section">
            {/* Payment Method */}
            <div className="payment-form-card">
              <h3 className="payment-form-title">
                Payment method
              </h3>
              <Dropdown
                options={paymentMethods}
                value={paymentInfo.paymentMethod}
                onChange={(value) => handlePaymentInfoChange('paymentMethod', value)}
                placeholder="Select"
                error={errors.paymentMethod}
              />
            </div>
            {/* Payment Info */}
            <div className="payment-form-card">
              <h3 className="payment-form-title">
                Payment Info
              </h3>
              <div className="payment-form-fields">
                <InputField
                  label="Card Number"
                  type="text"
                  value={paymentInfo.cardNumber}
                  onChange={(e) => handlePaymentInfoChange('cardNumber', e.target.value)}
                  placeholder="Enter card number"
                  error={errors.cardNumber}
                  maxLength={19}
                />
                <InputField
                  label="Expiration Date"
                  type="text"
                  value={paymentInfo.expirationDate}
                  onChange={(e) => handlePaymentInfoChange('expirationDate', e.target.value)}
                  placeholder="MM/YY"
                  error={errors.expirationDate}
                  maxLength={5}
                />
                <InputField
                  label="Security Code"
                  type="text"
                  value={paymentInfo.securityCode}
                  onChange={(e) => handlePaymentInfoChange('securityCode', e.target.value)}
                  placeholder="CVV"
                  error={errors.securityCode}
                  maxLength={4}
                />
              </div>
              {/* Pay Button */}
              <div className="payment-form-submit">
                <Button
                  onClick={handlePayment}
                  disabled={isProcessing || paymentDone}
                  className="payment-form-submit-button"
                >
                  {paymentDone
                    ? 'Payment Done'
                    : isProcessing
                      ? 'Processing...'
                      : `Pay ${appointmentData.subtotal}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
        {/* Footer Note */}
        <div className="payment-footer-note">
          <p className="payment-footer-text">
            *Once the transaction is completed, the fees incurred cannot be refunded.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;